import { Injectable } from '@angular/core';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { v4 } from 'uuid';
import { onActions } from '../../utils/helpers/redux.helper';
import { Account } from '../account-data/account.model';
import { Client } from '../client-data/client.model';
import { IMessageDeliveryData } from '../message-delivery/message-delivery.interface';
import { Settings } from '../settings-data/settings.model';
import { StatementInfo } from './models/statement-info.model';
import {
  ActionsEnum,
  fetchStatementInfo,
  fetchStatementMetrics,
  refreshStatementInfo,
  sendStatement,
  updateStatementInfo,
  updateStatementMetrics,
} from './statement-data.actions';
import { StatementDataBuilder } from './statement-data.builder';
import { IStatementData } from './statement-data.data';
import { List } from 'immutable';
import * as fromInvoices from '../invoice-data/invoice-data.actions';
import { EntityTypesEnum } from '../../app.enum';
import { Invoice } from '../invoice-data/invoice.model';
import { Document } from '../document-data/document.model';
import { DocumentTypesEnum } from '../document-data/document-data.data';

@Injectable()
export class StatementDataDispatcher {
  constructor(
    private _actions: Actions,
    private _store: Store<any>,
    private _statementDataBuilder: StatementDataBuilder
  ) {}

  fetchStatementMetrics(client: Client) {
    const dispatchId = v4();

    this._store.dispatch(
      fetchStatementMetrics(dispatchId, client.id, {
        client_id: client.id,
        client_billing_name: client.billingName,
      })
    );
  }

  fetchStatement(
    clientId: string,
    startDate: string,
    endDate: string,
    unpaidOnly: boolean
  ) {
    const dispatchId = v4();

    this._store.dispatch(
      fetchStatementInfo(dispatchId, clientId, startDate, endDate, unpaidOnly)
    );
  }

  refreshStatement(clientId: string) {
    const dispatchId = v4();

    this._store.dispatch(refreshStatementInfo(dispatchId, clientId));
  }

  resetStatementMetrics(clientId: string) {
    this._store.dispatch(updateStatementMetrics(clientId, null));
  }

  resetStatement(clientId: string) {
    this._store.dispatch(updateStatementInfo(clientId, null));
  }

  sendStatement(
    statementInfo: StatementInfo,
    settings: Settings,
    account: Account,
    cardPaymentsFeatureEnabled: boolean,
    paypalFeatureEnabled: boolean,
    messageDeliveryData: IMessageDeliveryData
  ): Observable<any> {
    const dispatchId = v4();
    const payload: IStatementData = this._statementDataBuilder.buildSendStatementPayload(
      statementInfo,
      account.email,
      settings.payments,
      cardPaymentsFeatureEnabled,
      paypalFeatureEnabled,
      messageDeliveryData
    );

    this._store.dispatch(
      sendStatement(dispatchId, statementInfo.statement.clientId, payload)
    );

    return onActions(
      this._actions,
      [ActionsEnum.SEND_STATEMENT_SUCCESS, ActionsEnum.SEND_STATEMENT_ERROR],
      dispatchId
    );
  }

  bulkMarkAsPaid(documents: List<Document>): Observable<any> {
    const fromInvoiceActions = fromInvoices.ActionsEnum;
    const invoices = documents
      .filter(
        (document: Document) =>
          document.documentType === DocumentTypesEnum.invoice
      )
      .map((document: Document) => new Invoice(document)) as List<Invoice>;

    this._store.dispatch(
      fromInvoices.batchMarkInvoiceAsPaidStore(
        EntityTypesEnum.invoice,
        invoices
      )
    );

    this._store.dispatch(
      fromInvoices.batchMarkInvoiceAsPaidNetwork(
        EntityTypesEnum.invoice,
        invoices
      )
    );

    return onActions(this._actions, [
      fromInvoiceActions.BATCH_MARK_INVOICE_AS_PAID_NETWORK_SUCCESS,
      fromInvoiceActions.BATCH_MARK_INVOICE_AS_PAID_NETWORK_ERROR,
    ]);
  }
}
